import { API_BASE_URL, DEFAULT_COMPANY_ID } from './config';
import { apiFetch } from './apiFetch';

export type MetricDefinitionDto = {
  id: string;
  companyId: string;
  code: string;
  name: string;
  type: 'likert_5' | 'likert_7' | 'numeric' | 'boolean' | 'open_text';
  config: Record<string, unknown>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type MetricListResponse = {
  success: boolean;
  data?: {
    definitions: MetricDefinitionDto[];
  };
  error?: string;
};

export type MetricSnapshotDto = {
  id: string;
  metricDefinitionId: string;
  subjectUserId: string;
  value: number | boolean | string;
  source: string;
  metadata: Record<string, unknown>;
  capturedAt: string;
  createdAt: string;
};

export type MetricSnapshotListResponse = {
  success: boolean;
  data?: {
    snapshots: MetricSnapshotDto[];
  };
  error?: string;
};

export async function listMetricDefinitions(
  companyId = DEFAULT_COMPANY_ID,
  includeInactive = false
): Promise<MetricListResponse> {
  const query = new URLSearchParams({
    includeInactive: includeInactive ? 'true' : 'false',
  }).toString();

  const response = await apiFetch(`${API_BASE_URL}/api/metrics/company/${companyId}/definitions?${query}`, {
    method: 'GET',
  });

  return response.json();
}

export async function createMetricDefinition(input: {
  companyId?: string;
  code: string;
  name: string;
  type: 'likert_5' | 'likert_7' | 'numeric' | 'boolean' | 'open_text';
}): Promise<{ success: boolean; data?: { definition: MetricDefinitionDto }; error?: string }> {
  const response = await apiFetch(`${API_BASE_URL}/api/metrics/definitions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      companyId: input.companyId ?? DEFAULT_COMPANY_ID,
      code: input.code,
      name: input.name,
      type: input.type,
    }),
  });

  return response.json();
}

export async function createMetricSnapshot(input: {
  companyId?: string;
  metricDefinitionId: string;
  subjectUserId: string;
  value: number | boolean | string;
  source?: string;
}): Promise<{ success: boolean; data?: { snapshot: MetricSnapshotDto }; error?: string }> {
  const response = await apiFetch(`${API_BASE_URL}/api/metrics/snapshots`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      companyId: input.companyId ?? DEFAULT_COMPANY_ID,
      metricDefinitionId: input.metricDefinitionId,
      subjectUserId: input.subjectUserId,
      value: input.value,
      source: input.source ?? 'manual',
    }),
  });

  return response.json();
}

export async function listMetricSnapshots(
  companyId: string,
  userId: string
): Promise<MetricSnapshotListResponse> {
  const response = await apiFetch(`${API_BASE_URL}/api/metrics/company/${companyId}/users/${userId}/snapshots`, {
    method: 'GET',
  });

  return response.json();
}

// ─── Aggregate types ──────────────────────────────────────────────────────────

export type MetricDivisionBreakdown = {
  divisionId: string;
  name: string;
  avg: number;
  employeeCount: number;
};

export type MetricTopEmployee = {
  userId: string;
  name: string;
  value: number;
};

export type MetricAggregateDto = {
  metricDefinitionId: string;
  code: string;
  name: string;
  companyAvg: number;
  trend: 'up' | 'down' | 'flat';
  byDivision: MetricDivisionBreakdown[];
  topEmployees: MetricTopEmployee[];
};

export type MetricAggregateResponse = {
  success: boolean;
  data?: {
    aggregates: MetricAggregateDto[];
  };
  error?: string;
};

export async function getMetricAggregate(companyId: string): Promise<MetricAggregateResponse> {
  const response = await apiFetch(`${API_BASE_URL}/api/metrics/company/${companyId}/aggregate`, {
    method: 'GET',
  });

  return response.json();
}
